/**
 * 🎯 **Pointing at a living thing** — the geometry, once, for everything that
 * aims at a creature.
 *
 * ## 🔴 WHY THIS FILE EXISTS, AND IT IS THIS REPO'S MOST-REPEATED FAULT
 *
 * **`spearTargetFrom` had this arithmetic inside it**, and T261 needed the same
 * question asked about a sheep: *is the thing under my crosshair within reach?*
 *
 * ⚠️ **Copying it would have been the sixth time in this repo that a SECOND WAY
 * IN FORGOT WHAT THE FIRST ONE DOES.** `06_Traps.md` names it by that title, and
 * `14_The_Animal_System.md` §③ records the last one in detail: the collar was
 * written down twice, **so the picture and the game drew different sheep**, and
 * tuning the dial would have moved one without the other.
 *
 * 🔴 **The failure mode here would have been worse than a wrong picture.** Two
 * copies of an aim test drift by a sign or a bound, and then *"I am clearly
 * looking at it and nothing happens"* is true on one path and false on the
 * other — **with nothing raised anywhere.**
 *
 * ## ⚠️ What is DELIBERATELY still separate
 *
 * ✅ **Which creatures are candidates at all**, and **how big a target each
 * one is.** Those are the two things a spear and a pair of hands genuinely
 * disagree about, and they are passed in.
 *
 * 🔴 **`spear.ts`'s own comment is the reason that matters:** *"Anything that is
 * not one of the four fish is ignored, rather than being spearable by default.
 * **The day a sheep exists, it must not become catchable because nobody
 * remembered this function.**"* 👉 **That day is today.** A shared *geometry*
 * keeps that promise; a shared *candidate list* would have broken it.
 */

import type { Vec3 } from './creature.ts';

/** Anything that can be pointed at. The wire's `creatures` shape. */
export interface AimTarget {
  readonly id: string;
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * 🎯 **The nearest thing under the crosshair, or `undefined`.**
 *
 * A target counts if it is **in front of you**, **within `reach`**, and the
 * crosshair passes within the radius `radiusOf` gives it. Of everything that
 * qualifies, **the nearest wins** — one creature behind another cannot be
 * reached through it.
 *
 * ⚠️ **`y` on the wire is the FEET**, the same convention players use. A caller
 * whose creature is tall enough for that to matter must say so in `radiusOf` or
 * lift its own `y` before calling — **said here rather than left to be
 * discovered**, because it is exactly the sort of half-metre this project has
 * shipped before.
 *
 * @param radiusOf how forgiving the aim is for this target, in metres.
 *   **`0` or less excludes it**, which is how a caller says *"not a candidate"*
 *   without a second list.
 */
export function aimAt(
  eye: Vec3,
  look: Vec3,
  targets: readonly AimTarget[],
  reach: number,
  radiusOf: (target: AimTarget) => number,
): AimTarget | undefined {
  let best: AimTarget | undefined;
  let bestAlong = Infinity;

  for (const t of targets) {
    const radius = radiusOf(t);
    /* 🔴 Not a candidate. The caller's own species table decided, not this
       function — see the header. */
    if (!(radius > 0)) continue;

    const vx = t.x - eye.x;
    const vy = t.y - eye.y;
    const vz = t.z - eye.z;

    /* How far along the crosshair it sits. Behind you is not a target. */
    const along = vx * look.x + vy * look.y + vz * look.z;
    if (along <= 0 || along > reach) continue;

    /* How far the crosshair misses its centre by. */
    const px = vx - look.x * along;
    const py = vy - look.y * along;
    const pz = vz - look.z * along;
    if (Math.hypot(px, py, pz) > radius) continue;

    if (along < bestAlong) {
      bestAlong = along;
      best = t;
    }
  }

  return best;
}
